import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Award,
  Calendar,
  ChevronDown,
  ChevronRight,
  Filter,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  Search,
  Trophy,
  Users,
  XCircle,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Toast, type ToastKind } from "@/components/Toast";
import { api } from "@/lib/api";
import { formatItalianDateShort, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function ClientsHistory() {
  const qc = useQueryClient();
  const [minTournaments, setMinTournaments] = useState(2);
  const [since, setSince] = useState("");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [hideUnmatched, setHideUnmatched] = useState(false);
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(null);

  // Debounce
  if (search !== searchDebounced) {
    setTimeout(() => setSearchDebounced(search), 300);
  }

  const q = useQuery({
    queryKey: [
      "past-participants",
      { min_tournaments: minTournaments, since, q: searchDebounced },
    ],
    queryFn: () =>
      api.pastParticipants({
        min_tournaments: minTournaments,
        since: since || undefined,
        q: searchDebounced || undefined,
        limit: 1000,
      }),
    staleTime: 5 * 60 * 1000,
  });

  const refresh = useMutation({
    mutationFn: () => api.refreshPastParticipants(365),
    onSuccess: (res) => {
      setToast({
        kind: "success",
        message: `Refresh ok: ${res.players_count} giocatori da ${res.tournaments_count} tornei`,
      });
      qc.invalidateQueries({ queryKey: ["past-participants"] });
    },
    onError: (e) => {
      const msg =
        (e as { body?: { detail?: string }; message?: string })?.body?.detail ??
        (e as Error)?.message ??
        String(e);
      setToast({ kind: "error", message: `Errore refresh: ${msg}` });
    },
  });

  const players = (q.data?.players ?? []).filter(
    (p) => !hideUnmatched || p.client_matched,
  );

  const resetFilters = () => {
    setMinTournaments(2);
    setSince("");
    setSearch("");
    setSearchDebounced("");
    setHideUnmatched(false);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Back */}
      <div>
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Anagrafica clienti
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <Trophy className="h-6 w-6 text-moving-500" />
            Giocatori storici
          </h1>
          <p className="text-sm text-slate-500">
            {q.data
              ? `${formatNumber(q.data.players_count)} giocatori da ${
                  q.data.tournaments_count
                } tornei · cache aggiornata ${
                  q.data.cache_age_hours < 1
                    ? "appena ora"
                    : `${q.data.cache_age_hours.toFixed(1)}h fa`
                }`
              : "Caricamento storico…"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh.mutate()}
            loading={refresh.isPending}
            title="Scarica di nuovo tutto lo storico dagli ultimi 365gg (può richiedere qualche minuto)"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh cache
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="rounded-md border border-court-200 bg-court-50 p-3 text-xs text-court-900">
        <strong>Come funziona:</strong> aggreghiamo tutti gli iscritti ai tornei
        Playtomic degli ultimi 365 giorni e li JOIN-iamo con l'anagrafica
        clienti per email/telefono. Cache automatica ogni notte alle 04:00;
        puoi forzare un refresh con il bottone. I giocatori senza
        corrispondenza nell'anagrafica sono etichettati "—".
      </div>

      {/* Stats mini */}
      {q.data ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <MiniStat
            label="Giocatori (filtri)"
            value={formatNumber(players.length)}
            hint={`${q.data.players_count} pre-filter testo`}
            icon={<Users className="h-4 w-4" />}
          />
          <MiniStat
            label="Matched clienti"
            value={formatNumber(q.data.matched_with_clients)}
            hint={
              q.data.players_count > 0
                ? `${Math.round(
                    (q.data.matched_with_clients / q.data.players_count) * 100,
                  )}%`
                : ""
            }
            icon={<Mail className="h-4 w-4" />}
          />
          <MiniStat
            label="Tornei indicizzati"
            value={formatNumber(q.data.tournaments_count)}
            hint={`ultimi ${q.data.days_back}gg`}
            icon={<Trophy className="h-4 w-4" />}
          />
          <MiniStat
            label="Senza anagrafica"
            value={formatNumber(q.data.missing_from_clients)}
            hint="non nel CSV clienti"
            icon={<XCircle className="h-4 w-4" />}
          />
        </div>
      ) : null}

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Filter className="h-4 w-4" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto_auto_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca nome o email…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Min tornei</Label>
            <Input
              type="number"
              min={1}
              max={50}
              value={minTournaments}
              onChange={(e) => setMinTournaments(parseInt(e.target.value) || 1)}
              className="w-20"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Da</Label>
            <Input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="w-40"
            />
          </div>
          <label className="flex items-center gap-2 text-xs whitespace-nowrap">
            <input
              type="checkbox"
              checked={hideUnmatched}
              onChange={(e) => setHideUnmatched(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-moving-500 focus:ring-moving-500"
            />
            Solo con anagrafica
          </label>
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            Reset
          </Button>
        </CardBody>
      </Card>

      {/* Tabella */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Classifica partecipazioni
            <span className="text-xs font-normal text-slate-500">
              · top fan ultimi 365gg
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Caricamento storico… (può richiedere ~30s al primo hit)
            </div>
          ) : q.isError ? (
            <div className="p-10 text-center text-sm text-red-700">
              Errore: {String(q.error)}
            </div>
          ) : players.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Nessun giocatore trovato con i filtri correnti.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="w-8 px-2 py-2"></th>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Giocatore</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-center">Livello</th>
                    <th className="px-3 py-2 text-center">Tornei</th>
                    <th className="px-3 py-2 text-center">Ultimo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {players.map((p, i) => {
                    const open = expandedUserId === p.user_id;
                    return (
                      <>
                        <tr
                          key={p.user_id}
                          className={cn(
                            "hover:bg-slate-50 cursor-pointer",
                            !p.client_matched && "bg-amber-50/40",
                          )}
                          onClick={() =>
                            setExpandedUserId(open ? null : p.user_id)
                          }
                        >
                          <td className="w-8 px-2 text-slate-400">
                            {open ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-400">
                            {i + 1}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {p.picture ? (
                                <img
                                  src={p.picture}
                                  alt=""
                                  className="h-7 w-7 shrink-0 rounded-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-500">
                                  {p.full_name.slice(0, 2).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <div className="font-medium text-slate-900">
                                  {p.full_name}
                                </div>
                                {!p.client_matched ? (
                                  <div className="text-[10px] text-amber-700">
                                    ⚠ non in anagrafica CSV
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {p.email ? (
                              <span
                                className={cn(
                                  "text-xs",
                                  p.unsubscribed_email && "line-through text-slate-400",
                                )}
                                title={
                                  p.unsubscribed_email ? "Opted-out email" : ""
                                }
                              >
                                {p.email}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {p.phone ? (
                              <span
                                className={cn(
                                  "font-mono text-xs",
                                  p.unsubscribed_whatsapp &&
                                    "line-through text-slate-400",
                                )}
                                title={
                                  p.unsubscribed_whatsapp
                                    ? "Opted-out WhatsApp"
                                    : ""
                                }
                              >
                                {p.phone}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {p.level_value !== null ? (
                              <span className="inline-flex items-center gap-1 rounded bg-court-50 px-1.5 py-0.5 text-xs text-court-700">
                                <Award className="h-3 w-3" />
                                {p.level_value.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center gap-1 rounded-full bg-moving-100 px-2 py-0.5 text-xs font-semibold text-moving-700">
                              {p.tournaments_count}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-slate-500">
                            {p.last_tournament_date
                              ? formatItalianDateShort(p.last_tournament_date)
                              : "—"}
                          </td>
                        </tr>
                        {open ? (
                          <tr key={p.user_id + "-expand"}>
                            <td colSpan={8} className="bg-slate-50 px-6 py-4">
                              <div className="space-y-2">
                                <div className="text-xs font-semibold uppercase text-slate-500">
                                  Tornei giocati ({p.tournaments.length})
                                </div>
                                <ul className="space-y-1 text-xs">
                                  {p.tournaments.map((t) => (
                                    <li
                                      key={t.activity_id}
                                      className="flex items-center gap-3 rounded bg-white px-2 py-1.5"
                                    >
                                      <Calendar className="h-3 w-3 shrink-0 text-slate-400" />
                                      <span className="font-mono text-slate-500">
                                        {formatItalianDateShort(t.date)}
                                      </span>
                                      <span className="flex-1 truncate text-slate-700">
                                        {t.name}
                                      </span>
                                      {t.price ? (
                                        <span className="font-mono text-slate-500">
                                          {t.price}
                                        </span>
                                      ) : null}
                                      {t.paid ? (
                                        <span className="rounded bg-moving-100 px-1.5 text-[10px] font-semibold text-moving-700">
                                          paid
                                        </span>
                                      ) : null}
                                      <a
                                        href={`https://app.playtomic.io/tournaments/${t.activity_id}?utm_source=manager`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-court-600 hover:underline"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Playtomic ↗
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                                {p.email || p.phone ? (
                                  <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                                    {p.email && !p.unsubscribed_email ? (
                                      <a
                                        href={`mailto:${p.email}`}
                                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100"
                                      >
                                        <Mail className="h-3 w-3" />
                                        Email
                                      </a>
                                    ) : null}
                                    {p.phone && !p.unsubscribed_whatsapp ? (
                                      <a
                                        href={`https://wa.me/${p.phone.replace(/[^0-9]/g, "")}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100"
                                      >
                                        <MessageCircle className="h-3 w-3" />
                                        WhatsApp
                                      </a>
                                    ) : null}
                                    {p.phone ? (
                                      <a
                                        href={`tel:${p.phone.replace(/\s/g, "")}`}
                                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100"
                                      >
                                        <Phone className="h-3 w-3" />
                                        Chiama
                                      </a>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}

function MiniStat({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-1 text-xs uppercase text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 text-lg font-semibold text-slate-900">{value}</div>
      {hint ? <div className="text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}
